import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

// Configura axios para usar credenciales en todas las solicitudes por defecto
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Loading state for initial check
  const [folderID, setFolderID] = useState(null); // Estado para el folderID
  const [user, setUser] = useState(null); // Estado para almacenar información del usuario
  // Tipo de cambio (c33) y fecha de vigencia (c32) almacenados en sesión
  const [exchangeRate, setExchangeRate] = useState(null);
  const [exchangeRateDate, setExchangeRateDate] = useState(null);

  // Función para verificar estado de autenticación con el backend
  const checkAuthStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      console.log('Verificando estado de autenticación con el backend...');
      const response = await axios.get(`${BACKEND_URL}/api/auth/status`);
      console.log('Respuesta status:', response.data);
      
      setIsAuthenticated(response.data.isAuthenticated);
      
      if (response.data.isAuthenticated && response.data.folderID) {
        setFolderID(response.data.folderID);
        console.log('AuthContext: FolderID recuperado de sesión:', response.data.folderID);
      }
      
      // Recuperar información del usuario si está disponible
      if (response.data.isAuthenticated && response.data.user) {
        setUser(response.data.user);
        console.log('AuthContext: Información de usuario recuperada de sesión:', response.data.user);
        // Log if readOnly status is present or missing in the status response
        if (response.data.user.hasOwnProperty('isReadOnly')) {
          console.log('AuthContext: isReadOnly recuperado de sesión:', response.data.user.isReadOnly);
        } else {
          console.warn('AuthContext: isReadOnly NO se encontró en la respuesta de /status. El usuario podría no tener el estado correcto.');
          // Ensure user object has isReadOnly, defaulting to false if missing
          setUser(prevUser => ({
            ...prevUser, // Keep existing user data like nombre
            isReadOnly: false // Default to false if not provided by backend
          }));
        }
      }
      
      // Guardar tipo de cambio y limpiar la vigencia cuando el backend usa Banxico
      if (response.data.isAuthenticated) {
        if (response.data.exchangeRate !== undefined && response.data.exchangeRate !== null && response.data.exchangeRate !== '') {
          setExchangeRate(response.data.exchangeRate);
          console.log('AuthContext: Tipo de cambio recuperado de sesión:', response.data.exchangeRate);
        } else {
          setExchangeRate(null);
        }

        if (response.data.exchangeRateDate) {
          setExchangeRateDate(response.data.exchangeRateDate);
          console.log('AuthContext: Fecha vigencia TC (c32) recuperada de sesión:', response.data.exchangeRateDate);
        } else {
          setExchangeRateDate(null);
        }
      }
    } catch (error) {
      console.error('AuthContext: Error checking auth status:', error);
      setIsAuthenticated(false);
      setFolderID(null);
      setUser(null);
      setExchangeRate(null);
      setExchangeRateDate(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Verificar autenticación al cargar la aplicación
  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  const login = useCallback((data) => {
    // Actualizado para guardar folderID del response cuando login es exitoso
    console.log('AuthContext: Estableciendo estado autenticado con datos:', data);
    setIsAuthenticated(true);
    
    if (data && data.folderID) {
      setFolderID(data.folderID);
      console.log('AuthContext: FolderID guardado:', data.folderID);
    } else {
      console.warn('AuthContext: No se recibió folderID en login');
    }
    
    // Guardar información del usuario si está disponible
    if (data && data.user) {
      setUser(data.user);
      console.log('AuthContext: Información de usuario guardada:', data.user);
    } else {
      console.warn('AuthContext: No se recibió información de usuario en login');
    }
    
    // Guardar tipo de cambio y limpiar la vigencia cuando el backend usa Banxico
    if (data && data.exchangeRate !== undefined && data.exchangeRate !== null && data.exchangeRate !== '') {
      setExchangeRate(data.exchangeRate);
      console.log('AuthContext: Tipo de cambio guardado tras login:', data.exchangeRate);
    } else {
      setExchangeRate(null);
    }
    if (data && data.exchangeRateDate) {
      setExchangeRateDate(data.exchangeRateDate);
      console.log('AuthContext: Fecha vigencia TC guardada tras login:', data.exchangeRateDate);
    } else {
      setExchangeRateDate(null);
    }
    
    console.log('AuthContext: Usuario logueado correctamente.');
  }, []);

  const logout = useCallback(async () => {
    console.log('AuthContext: Iniciando proceso de logout...');
    // Actualizar estado inmediatamente para una UI más responsiva
    setIsAuthenticated(false);
    setFolderID(null);
    setUser(null);
    setExchangeRate(null);
    setExchangeRateDate(null);
    
    try {
      // Llamar al backend para destruir la sesión
      await axios.post(`${BACKEND_URL}/api/auth/logout`);
      console.log('AuthContext: Logout exitoso en el backend.');
    } catch (error) {
      // Registrar error pero mantener el estado de frontend como deslogueado
      console.error('AuthContext: Error llamando logout en backend:', error);
    }
  }, []);

  // Exponer estado y funciones de autenticación
  const authContextValue = {
    isAuthenticated,
    isLoading,
    folderID,
    user,
    exchangeRate,
    exchangeRateDate,
    login,
    logout,
    checkAuthStatus // Exponemos esta función para poder llamarla desde componentes si es necesario
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook para usar el contexto de autenticación
export const useAuth = () => {
  return useContext(AuthContext);
}; 